import { useMutation, useQueries } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';

import { rolesAllowedInOrgGroups } from 'common/src/authorization/editAccess';
import { ArrayOperators } from 'common/src/query-params/operators';
import {
  IOrganization,
  IOrganizationGroup,
  IPage,
  IUser,
} from 'common/src/types/interfaces';

import {
  addOrganizationGroup,
  updateOrganizationGroup,
} from 'portal/src/api/OrganizationGroupsService';
import { getOrganizations } from 'portal/src/api/OrganizationsService';
import { getUsersService } from 'portal/src/api/UsersService';
import { FormContainer } from 'portal/src/components/common/forms/FormContainer';
import { MESSAGES } from 'portal/src/constants/snackbarMessages';
import useSnackbarHandler from 'portal/src/utils/Snackbar/useSnackbarHandler';

import { defaultValues, getFields } from './OrganizationGroupsForm.fields';

interface FormComponentProps {
  entity?: Partial<IOrganizationGroup>;
  handleBackAction: () => void;
}

const OrganizationGroupsForm = ({
  entity = {},
  handleBackAction,
}: FormComponentProps) => {
  const snackbarHandler = useSnackbarHandler();
  const {
    control,
    formState: { isDirty },
    handleSubmit,
  } = useForm({ defaultValues: defaultValues(entity) });

  const {
    mutate: tryAddOrganizationGroup,
    isPending: isAddOrganizationGroupPending,
  } = useMutation({
    mutationFn: async (payload: any) => addOrganizationGroup(payload),
    onSuccess: () => {
      snackbarHandler.success(MESSAGES.ORGANIZATION_GROUP.CREATED);
      handleBackAction();
    },
    onError: () => {
      snackbarHandler.error(MESSAGES.GENERIC.ERROR);
    },
  });

  const {
    mutate: tryUpdateOrganizationGroup,
    isPending: isUpdateOrganizationGroupPending,
  } = useMutation({
    mutationFn: async (payload: any) =>
      updateOrganizationGroup(entity.id, payload),
    onSuccess: () => {
      snackbarHandler.success(MESSAGES.ORGANIZATION_GROUP.UPDATED);
      handleBackAction();
    },
    onError: () => {
      snackbarHandler.error(MESSAGES.GENERIC.ERROR);
    },
  });

  const [
    { data: organizations, isFetching: isFetchingOrganizations },
    { data: users, isFetching: isFetchingUsers },
  ] = useQueries({
    queries: [
      {
        queryKey: ['organizations'],
        queryFn: async () => await getOrganizations(),
        initialData: { data: [] } as IPage<IOrganization>,
      },
      {
        queryKey: ['users'],
        queryFn: async () =>
          await getUsersService({
            allOrganizations: true,
            filters: [
              {
                field: 'userType',
                operator: ArrayOperators.IN,
                value: rolesAllowedInOrgGroups,
              },
            ],
          }),
        initialData: { data: [] } as IPage<IUser>,
      },
    ],
  });

  const submitForm = (data: Partial<IOrganizationGroup>) => {
    const formattedPayload = {
      ...data,
      organizations: data.organizations.map((orgId) => ({ id: orgId })),
      users: data.users.map((userId) => ({ id: userId })),
    };

    entity.id
      ? tryUpdateOrganizationGroup(formattedPayload)
      : tryAddOrganizationGroup(formattedPayload);
  };

  const formFieldsConfig = getFields(organizations.data, users.data);

  const isPending =
    isAddOrganizationGroupPending ||
    isFetchingOrganizations ||
    isFetchingUsers ||
    isUpdateOrganizationGroupPending;

  return (
    <FormContainer
      control={control}
      isDirty={isDirty}
      handleBackAction={handleBackAction}
      handleSubmit={handleSubmit((data) => submitForm(data))}
      header="Organization Group Information"
      isNew={!entity.id}
      isPending={isPending}
      mainFieldsConfig={formFieldsConfig}
    />
  );
};

export default OrganizationGroupsForm;
